import { useState } from "react";
import { ChevronLeftFilled, ChevronRightFilled } from "@fluentui/react-icons";
import styles from "./CarImageCarousel.module.css";

interface CarImageCarouselProps {
    images: {
        imagen_url?: string;
        image_url_2?: string;
        image_url_3?: string;
        image_url_4?: string;
        image_url_5?: string;
    };
    carTitle: string;
    onExpand?: (index: number) => void;
}

export const CarImageCarousel = ({ images, carTitle, onExpand }: CarImageCarouselProps) => {
    // Filter out empty/null images and limit to 5
    const imageUrls = [
        images.imagen_url,
        images.image_url_2,
        images.image_url_3,
        images.image_url_4,
        images.image_url_5,
    ].filter((url): url is string => !!url).slice(0, 5);
    



    const [currentIndex, setCurrentIndex] = useState(0);
    const totalImages = imageUrls.length;

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalImages - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev < totalImages - 1 ? prev + 1 : 0));
    };


    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const target = e.target as HTMLImageElement;
        target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23f3f4f6"%2F%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="18" fill="%239ca3af"%3EImagen no disponible%3C%2Ftext%3E%3C%2Fsvg%3E';
    };

    if (totalImages === 0) {
        return (
            <div className={styles.carouselContainer}>
                <div className={styles.mainImageContainer}>
                    <div className={styles.noImagePlaceholder}>
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 7C5 5.89543 5.89543 5 7 5H17C18.1046 5 19 5.89543 19 7V17C19 18.1046 18.1046 19 17 19H7C5.89543 19 5 18.1046 5 17V7Z" stroke="#9ca3af" strokeWidth="2"/>
                            <path d="M12 14C13.6569 14 15 12.6569 15 11C15 9.34315 13.6569 8 12 8C10.3431 8 9 9.34315 9 11C9 12.6569 10.3431 14 12 14Z" stroke="#9ca3af" strokeWidth="2"/>
                            <path d="M5 15L8.5 11.5L11 14L14.5 10.5L19 15" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Imagen no disponible</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.carouselContainer}>
            <div className={styles.mainImageContainer}>
                <img
                    src={imageUrls[currentIndex]}
                    alt={`${carTitle} - Imagen ${currentIndex + 1}`}
                    className={styles.mainImage}
                    loading="lazy"
                    decoding="async"
                    onError={handleImageError}
                />

                {totalImages > 1 && (
                    <>
                        <button
                            className={`${styles.navigationButtons} ${styles.prevButton}`}
                            onClick={handlePrevious}
                            aria-label="Imagen anterior"
                        >
                            <ChevronLeftFilled />
                        </button>
                        <button
                            className={`${styles.navigationButtons} ${styles.nextButton}`}
                            onClick={handleNext}
                            aria-label="Imagen siguiente"
                        >
                            <ChevronRightFilled />
                        </button>

                        <div className={styles.imageCounter}>
                            {currentIndex + 1}/{totalImages}
                        </div>
                    </>
                )}

                {/* Expand Button */}
                {onExpand && (
                    <button
                        className={styles.expandButton}
                        onClick={() => onExpand(currentIndex)}
                        aria-label="Ver imagen en pantalla completa"
                        title="Ver en pantalla completa"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M21 8V5a2 2 0 0 0-2-2h-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M3 16v3a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 21h3a2 2 0 0 0 2-2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                )}
            </div>

        </div>
    );
};